import ParsedText from "@/ui-lib/components/ParsedText";
import { createColumnHelper } from "@tanstack/react-table";
import { getCurrencyDisplayName } from "@ternary/api-lib/analytics/utils/NumberFormatUtils";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import { TenantEventEntity } from "@ternary/api-lib/core/types/Tenant";
import Table from "@ternary/api-lib/ui-lib/components/Table/Table";
import { formatDate } from "date-fns";
import React, { useMemo } from "react";
import copyText from "../copyText";

type TableData = {
  changedBy: string;
  currencyCode: CurrencyCode;
  date: string;
};

interface Props {
  isLoading: boolean;
  tenantEvents: TenantEventEntity[];
}

export default function AuditLogsTable({
  isLoading,
  tenantEvents,
}: Props): JSX.Element {
  const auditLogColumnHelper = createColumnHelper<TableData>();

  const columns = useMemo(
    () => [
      auditLogColumnHelper.accessor("changedBy", {
        header: copyText.auditLogTableHeader_changedBy,
        size: 230,
      }),
      auditLogColumnHelper.accessor("date", {
        cell: ({ row }) => {
          return !row.original.date
            ? copyText.notAvailable
            : formatDate(new Date(row.original.date), "MM/dd/yyyy");
        },
        header: copyText.auditLogTableHeader_date,
        size: 230,
      }),
      auditLogColumnHelper.accessor("currencyCode", {
        cell: ({ row }) => (
          <ParsedText
            text={copyText.auditLogTableData_event}
            components={{
              p: <p />,
              currencyCode: (
                <b>
                  {getCurrencyDisplayName(row.original.currencyCode, {
                    symbolOnly: true,
                  })}
                </b>
              ),
            }}
          />
        ),
        header: copyText.auditLogTableHeader_event,
        size: 340,
      }),
    ],
    []
  );

  const data = useMemo(() => {
    if (isLoading) return [];

    return tenantEvents.map((event) => ({
      changedBy: event.data.updatedBy ?? "",
      currencyCode: event.data.preferredCurrencyCode as CurrencyCode,
      date: event.createdAt ?? "",
    }));
  }, [tenantEvents, isLoading]);

  return (
    <Table
      columns={columns}
      data={data}
      initialState={{ sorting: [{ id: "email", desc: false }] }}
      isLoading={isLoading}
      showPagination
      sortable
    />
  );
}
